/**
 * Who hears about what, and how it reaches them.
 *
 * Mirrors `backend/src/notifications/notifications.types.ts` exactly — keep the
 * two files in step, and change neither without the other.
 */

export type NotificationChannel = 'email';

export type NotificationKind =
  | 'critical_alert'
  | 'weekly_digest'
  | 'daily_digest'
  | 'stance_flip'
  | 'scan_failed'
  | 'decision_approaching';

export type NotificationStatus = 'sent' | 'failed' | 'suppressed';

export const NOTIFICATION_KINDS: NotificationKind[] = [
  'critical_alert',
  'weekly_digest',
  'daily_digest',
  'stance_flip',
  'scan_failed',
  'decision_approaching',
];

/** What each kind is, in the words shown on the preferences page */
export const NOTIFICATION_LABELS: Record<
  NotificationKind,
  { title: string; description: string; immediate: boolean }
> = {
  critical_alert: {
    title: 'Critical alerts',
    description: 'A serious legal, corruption, protest or security item about someone you watch',
    immediate: true,
  },
  weekly_digest: {
    title: 'Weekly brief',
    description: 'What changed across monitoring and issues in the past week',
    immediate: false,
  },
  daily_digest: {
    title: 'Daily brief',
    description: 'The same, every working morning',
    immediate: false,
  },
  stance_flip: {
    title: 'Someone changes side',
    description: 'A stakeholder moves from supporting to opposing an issue, or back',
    immediate: true,
  },
  scan_failed: {
    title: 'Monitoring stopped working',
    description: 'A scan failed, so coverage is no longer being collected',
    immediate: true,
  },
  decision_approaching: {
    title: 'A decision is close',
    description: 'An issue you own is decided within the week',
    immediate: false,
  },
};

export interface SubscriptionView {
  kind: NotificationKind;
  channel: NotificationChannel;
  enabled: boolean;
  /** Local hours between which nothing but a critical alert is sent */
  quietFrom: number | null;
  quietTo: number | null;
}

export interface UpdateSubscriptionInput {
  kind: NotificationKind;
  channel?: NotificationChannel;
  enabled: boolean;
  quietFrom?: number | null;
  quietTo?: number | null;
}

/* ------------------------------------------------------------------ */
/* Delivery                                                            */
/* ------------------------------------------------------------------ */

/** Where mail goes out from. Stored in settings, never in the repository. */
export interface MailSettings {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  /** Write-only: reads return whether one is saved, never the value */
  password?: string;
  hasPassword?: boolean;
  fromName: string;
  fromAddress: string;
  /** Used to build links back into the app */
  appUrl: string;
  enabled: boolean;
}

export interface MailCheck {
  ok: boolean;
  message: string;
  latencyMs: number;
}

export interface OutgoingMessage {
  to: string;
  subject: string;
  /** Both are sent: some clients and most archives want the plain one */
  html: string;
  text: string;
}

export interface DeliveryResult {
  sent: number;
  suppressed: number;
  failed: number;
  /** Why nothing went out, when nothing did */
  skippedReason?: string;
}

export interface NotificationLogView {
  id: string;
  kind: NotificationKind;
  subject: string;
  status: NotificationStatus;
  detail: string | null;
  createdAt: string;
}
